import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  AlertTriangle,
  ShieldAlert,
  Phone,
  Mail,
  MessageSquare,
  Globe,
  Smartphone,
  Mailbox,
  Users,
  HelpCircle,
} from 'lucide-react'
import { fetchPublicScam, type Scam } from '@/lib/api'
import { usePageSeo } from '@/lib/page-seo'
import { resolveSite, siteBrand } from '@/lib/site'
import { useLocale } from '@/lib/locale'
import { useUi, type UiStrings } from '@/lib/i18n/ui'
import { getQatarScam } from '@/data/qatar-scams'
import { scamIcon } from '@/lib/scam-icons'
import { QATAR_BRAND } from '@/components/qatar/brand'
import ScamEntryActions from '@/components/ScamEntryActions'

export const Route = createFileRoute('/scam-library/$slug')({
  component: ScamPage,
})

/**
 * Channel labels + icons for the meta panel. Channel enum values come from
 * granguard-api's ScamChannel, kept loose (`Record<string, ...>`) so any
 * unknown values just fall through with a generic icon.
 */
const CHANNEL_META: Record<string, { label: keyof UiStrings; icon: typeof Phone }> = {
  PHONE: { label: 'channelPhone', icon: Phone },
  SMS: { label: 'channelSms', icon: MessageSquare },
  EMAIL: { label: 'channelEmail', icon: Mail },
  WEBSITE: { label: 'channelWebsite', icon: Globe },
  APP: { label: 'channelApp', icon: Smartphone },
  MAIL: { label: 'channelMail', icon: Mailbox },
  IN_PERSON: { label: 'channelInPerson', icon: Users },
  SOCIAL: { label: 'channelSocial', icon: Users },
  MESSAGING: { label: 'channelMessaging', icon: MessageSquare },
}

const RISK_META: Record<NonNullable<Scam['riskLevel']>, { label: keyof UiStrings; cls: string }> = {
  LOW: { label: 'riskLow', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  MEDIUM: { label: 'riskMedium', cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  HIGH: { label: 'riskHigh', cls: 'bg-orange-50 text-orange-700 ring-orange-200' },
  CRITICAL: { label: 'riskCritical', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
}

function ScamPage() {
  const { slug } = Route.useParams()
  const t = useUi()
  const locale = useLocale()
  const isQatar = resolveSite() === 'qatar'
  const remote = useQuery({
    queryKey: ['public-scam', slug],
    queryFn: () => fetchPublicScam(slug),
    retry: 0,
    enabled: !isQatar,
  })
  // The Qatar face reads its own bilingual set; a missing slug is an error.
  const local = isQatar ? getQatarScam(slug, locale) : null
  const data = isQatar ? local ?? undefined : remote.data
  const isLoading = !isQatar && remote.isLoading
  const isError = isQatar ? local === null : remote.isError

  // Generic title until the scam loads, then the real one; Googlebot waits
  // for the network idle before snapshotting, so it indexes the loaded state.
  const siteName = isQatar ? QATAR_BRAND.program[locale] : siteBrand().name
  usePageSeo({
    title: data ? `${data.title} · ${t.libraryTitle} · ${siteName}` : `${t.libraryTitle} · ${siteName}`,
    description: data?.description || undefined,
    canonicalPath: `/scam-library/${slug}`,
  })
  const TileIcon = scamIcon(data?.icon)

  return (
    <div className="mx-auto max-w-6xl px-6 pb-20 pt-10">
      <Link
        to="/scam-library"
        className="inline-flex items-center gap-1.5 text-base font-semibold text-violet-600 hover:text-violet-700"
      >
        <ArrowLeft className="h-4 w-4" />
        {t.backToLibrary}
      </Link>

      {isLoading && <p className="mt-12 text-base text-slate-400">{t.loading}</p>}
      {isError && (
        <p className="mt-12 text-base text-rose-500">
          {t.entryError}
        </p>
      )}

      {data && (
        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_18rem]">
          {/* ── Article column ─────────────────────────── */}
          <article>
            <header className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
              {data.image ? (
                <div className="shrink-0 self-start overflow-hidden rounded-2xl bg-gradient-to-br from-violet-100 to-violet-50">
                  <img
                    src={data.image}
                    alt=""
                    className="scam-illustration h-32 w-32 object-cover sm:h-40 sm:w-40"
                  />
                </div>
              ) : data.icon ? (
                <div className="scam-tile flex h-32 w-32 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1d1145] via-[#251a5a] to-[#3b2780] sm:h-40 sm:w-40">
                  <TileIcon className="h-14 w-14 text-white/90" strokeWidth={1.5} />
                </div>
              ) : null}
              <div className="min-w-0 flex-1">
                {data.scamCategory?.name && (
                  <p className="text-base font-semibold text-slate-500">
                    {data.scamCategory.name}
                  </p>
                )}
                <h1 className="mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
                  {data.title}
                </h1>
                {data.description && (
                  <p className="mt-4 max-w-2xl text-lg leading-relaxed text-slate-500">
                    {data.description}
                  </p>
                )}
              </div>
            </header>

            {data.content && (
              <div
                className={[
                  'mt-10 max-w-2xl text-[17px] leading-[1.7] text-slate-600',
                  // Headings: stronger weight, dark color, real hierarchy
                  '[&_h1]:mt-12 [&_h1]:mb-4 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:tracking-tight [&_h1]:text-slate-900',
                  '[&_h2]:mt-12 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-slate-900',
                  '[&_h3]:mt-10 [&_h3]:mb-2 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:tracking-tight [&_h3]:text-slate-900',
                  '[&_h4]:mt-8 [&_h4]:mb-2 [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-slate-900',
                  // First child should never have top margin
                  '[&>*:first-child]:mt-0',
                  // Paragraphs
                  '[&_p]:my-5',
                  // Lists
                  '[&_ul]:my-5 [&_ul]:list-disc [&_ul]:ps-6 [&_ul_li]:my-2 [&_ul_li]:ps-1 [&_ul_li::marker]:text-violet-500',
                  '[&_ol]:my-5 [&_ol]:list-decimal [&_ol]:ps-6 [&_ol_li]:my-2 [&_ol_li]:ps-1 [&_ol_li::marker]:font-semibold [&_ol_li::marker]:text-violet-600',
                  // Inline emphasis
                  '[&_strong]:font-semibold [&_strong]:text-slate-900',
                  '[&_em]:italic',
                  '[&_a]:text-violet-600 [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-violet-700',
                  // Blockquotes
                  '[&_blockquote]:my-8 [&_blockquote]:border-s-4 [&_blockquote]:border-violet-300 [&_blockquote]:bg-violet-50/40 [&_blockquote]:px-5 [&_blockquote]:py-3 [&_blockquote]:text-slate-700 [&_blockquote]:rounded-e-lg [&_blockquote_p]:my-2',
                  // Code
                  '[&_code]:rounded-md [&_code]:bg-slate-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[0.9em] [&_code]:font-mono [&_code]:text-slate-800',
                  '[&_pre]:my-6 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:bg-slate-900 [&_pre]:p-5 [&_pre]:text-sm [&_pre]:text-slate-100 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-slate-100',
                  // Horizontal rule
                  '[&_hr]:my-12 [&_hr]:border-slate-200',
                  // Images embedded in content
                  '[&_img]:my-8 [&_img]:rounded-lg',
                ].join(' ')}
                dangerouslySetInnerHTML={{ __html: data.content }}
              />
            )}
          </article>

          {/* ── Meta sidebar ─────────────────────────── */}
          <aside className="lg:sticky lg:top-8 lg:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-semibold text-slate-500">{t.atAGlance}</p>

              {data.riskLevel && RISK_META[data.riskLevel] && (
                <div className="mt-3">
                  <p className="text-sm text-slate-400">{t.riskLevel}</p>
                  <span
                    className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-semibold ring-1 ${RISK_META[data.riskLevel].cls}`}
                  >
                    <ShieldAlert className="h-3 w-3" />
                    {t[RISK_META[data.riskLevel].label] as string}
                  </span>
                </div>
              )}

              {data.channels && data.channels.length > 0 && (
                <div className="mt-5">
                  <p className="text-sm text-slate-400">{t.whereYoullSeeIt}</p>
                  <ul className="mt-2 space-y-1.5">
                    {data.channels.map(ch => {
                      const meta = CHANNEL_META[ch]
                      const label = meta ? (t[meta.label] as string) : ch.toLowerCase()
                      const Icon = meta?.icon ?? HelpCircle
                      return (
                        <li
                          key={ch}
                          className="flex items-center gap-2 text-base text-slate-600"
                        >
                          <Icon className="h-3.5 w-3.5 text-violet-500" />
                          <span className="capitalize">{label}</span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {data.scamCategory?.name && (
                <div className="mt-5">
                  <p className="text-sm text-slate-400">{t.category}</p>
                  <p className="mt-1 text-base font-medium text-slate-700">
                    {data.scamCategory.name}
                  </p>
                </div>
              )}

              {data.tags && data.tags.length > 0 && (
                <div className="mt-5">
                  <p className="text-sm text-slate-400">{t.tags}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {data.tags.map(t => (
                      <span
                        key={t.id}
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-sm text-slate-600"
                      >
                        {t.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 rounded-2xl bg-violet-50/60 p-5 ring-1 ring-violet-200/60">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-base font-semibold text-slate-900">
                    {t.happenedToYou}
                  </p>
                  <p className="mt-1 text-base leading-snug text-slate-600">
                    {t.happenedBody}
                  </p>
                  <Link
                    to="/basics/$basicsSlug"
                    params={{ basicsSlug: 'if-youre-scammed' }}
                    className="mt-3 inline-flex text-base font-semibold text-violet-700 hover:text-violet-800"
                  >
                    {t.openChapter6}
                  </Link>
                </div>
              </div>
            </div>

            <ScamEntryActions scam={data} />
          </aside>
        </div>
      )}
    </div>
  )
}
